import { useCallback } from 'react';

import { Stack } from '@mui/material';

import ContentSelector from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import { setSmsContent } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, SmsSendInputState } from 'domains/campaigns/types';
import useSmses from 'hooks/queries/useSmses';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const ContentWrapper = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
    onContentUnavailableChange,
}: Props) => {
    const currentSend = campaignState.sends[activeSendIndex];
    const { content } = currentSend as SmsSendInputState;

    const { data: smses = [], isLoading: isLoadingSmses } = useSmses();

    const handleContentChange = useCallback(
        (contentId: number | null, contentName: string) => {
            dispatchCampaignAction(setSmsContent({ contentId, contentName }, activeSendIndex));

            // Update preview if needed
            if (!contentId) {
                setContentPreviewId(null);
            } else if (contentPreviewId) {
                setContentPreviewId(contentId);
            }
        },
        [activeSendIndex, contentPreviewId, dispatchCampaignAction, setContentPreviewId]
    );

    const handlePreviewChange = useCallback(() => {
        setContentPreviewId(contentPreviewId ? null : content.id || null);
    }, [contentPreviewId, content.id, setContentPreviewId]);

    return (
        <Stack rowGap={5}>
            <ContentSelector
                contentType={CampaignChannels.SMS}
                contentId={content.id}
                contentList={smses}
                onContentChange={handleContentChange}
                isLoading={isLoadingSmses}
                isPreviewVisible={!!contentPreviewId}
                onPreviewChange={handlePreviewChange}
                onContentUnavailableChange={onContentUnavailableChange}
            />
        </Stack>
    );
};

export default ContentWrapper;
